import { useState } from 'react';
import { AppLink } from '@/shared/components/AppLink';
import { Menu, Search as SearchIcon } from 'lucide-react';
import HamburgerMenu from '@/shared/features/menu/presentation/components/HamburgerMenu';
import { Logo } from '@/shared/icons/logo';
import { StoreIcon, CartIcon } from '@/shared/icons/icons';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';

interface HeaderMobileProps {
  cartCount: number;
  wishlistCount: number;
  isLoggedIn: boolean;
  userName?: string;
  onSearch: (query: string) => void;
  onSearchIconClick: () => void;
  onOpenLogin?: (mode?: 'login' | 'signup') => void;
}

export default function HeaderMobile({ cartCount, onSearch: _onSearch, onSearchIconClick, onOpenLogin }: HeaderMobileProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { analytics, isInitialized } = useAnalytics();

  const handleSearchIconClick = () => {
    if (analytics && isInitialized) {
      try {
        analytics.trackEvent('search_icon_click', {
          location: 'header_mobile',
          action: 'open_search_modal'
        });
      } catch (error) {
        console.error('Error tracking search icon click:', error);
      }
    } else {
      console.warn('Analytics not initialized yet, event not tracked');
    }
    onSearchIconClick();
  };

  return (
    <>
      <div className="px-[15px]">
        <div className="flex items-center justify-between gap-3 py-3">
          {/* Left: Hamburger Menu */}
          <button
            type="button"
            onClick={() => setIsMenuOpen(true)}
            className="flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors"
            aria-label="Menu"
          >
            <Menu className="h-6 w-6" />
          </button>

          {/* Center: Logo */}
          <div className="logo flex-1 flex justify-start">
            <AppLink
              to="/"
              eventName="BlueStone Logo Clicked"
              className="flex items-center hover:opacity-80 transition-opacity"
              aria-label="BlueStone Logo"
            >
              <Logo showLink={false} className="h-auto w-full max-w-[160px] min-w-[120px] text-gray-900" />
            </AppLink>
          </div>

          {/* Right: Icons */}
          <div className="flex items-center gap-4">
            {/* Store Locator */}
            <AppLink
              to="/store.html"
              className="flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors"
              aria-label="Store Locator"
            >
              <StoreIcon className="h-6 w-6" />
            </AppLink>

            {/* Cart */}
            <AppLink
              to="/order"
              shouldRefresh
              className="relative flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors"
              aria-label="Cart"
            >
              <CartIcon className="h-6 w-6 text-gray-600 group-hover:scale-110 transition-transform group-hover:text-hover" />
              {cartCount > 0 ? (
                <span className="absolute -top-2 -right-2 flex items-center justify-center min-w-[24px] h-5 px-1 rounded-md border border-gray-700 bg-white text-gray-700 text-xs font-semibold tabular-nums group-hover:border-hover group-hover:text-hover transition-colors">
                  {cartCount}
                </span>
              ) : null}
            </AppLink>

            {/* Search Icon */}
            <button
              onClick={handleSearchIconClick}
              className="flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors"
              aria-label="Search"
            >
              <SearchIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Hamburger Menu */}
      <HamburgerMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        onOpenLogin={onOpenLogin}
      />
    </>
  );
}
